import { getMessaging, getToken, isSupported, onMessage } from "firebase/messaging";
import { firebaseApp } from "./firebase";
import { resolveNotificationLink } from "./utils/notificationRoutes";

const SW_PATH = "/firebase-messaging-sw.js";

let cachedRegistration = null;
let cachedMessaging = null;
 
function getVapidKey() {
  return (process.env.REACT_APP_FIREBASE_VAPID_KEY || "BMRoBOo4saJG_DZXFzi4Mf_KnE62TVle9Ry6iVUJ8icNswrdqrd8za03eZHWI1vqd4MhTbSiV7Sb8ktglPGVbXU").trim();
}

export function hasFcmVapidKey() {
  return Boolean(getVapidKey());
}

export function logFcmToken(token, source = "FCM") {
  if (token) {
    console.log(`[${source}] FCM token:`, token);
  }
}

export function logNotificationPayload(payload, source = "FCM message") {
  console.group(`[${source}] Notification received`);
  console.log("Full payload:", payload);
  console.log("notification:", payload?.notification);
  console.log("data:", payload?.data);
  console.log("messageId:", payload?.messageId || payload?.fcmMessageId);
  console.groupEnd();
}

async function waitForServiceWorkerActive(registration, maxMs = 3000) {
  if (!registration) return null;
  if (registration.active) return registration;

  const installing = registration.installing || registration.waiting;
  if (!installing) return registration;

  await Promise.race([
    new Promise((resolve) => {
      const done = () => resolve();
      if (installing.state === "activated") {
        done();
        return;
      }
      installing.addEventListener("statechange", () => {
        if (installing.state === "activated") done();
      });
    }),
    new Promise((resolve) => setTimeout(resolve, maxMs)),
  ]);

  return registration;
}

export async function registerMessagingServiceWorker() {
  if (cachedRegistration?.active) return cachedRegistration;
  if (!("serviceWorker" in navigator)) return null;

  try {
    const existing = await navigator.serviceWorker.getRegistration(SW_PATH);
    if (existing?.active) {
      cachedRegistration = existing;
      return existing;
    }
    const registration = await navigator.serviceWorker.register(SW_PATH);
    cachedRegistration = await waitForServiceWorkerActive(registration);
    return cachedRegistration;
  } catch (e) {
    console.error("[FCM] Service worker registration failed:", e);
    return null;
  }
}

function getOrCreateMessaging() {
  if (!cachedMessaging) {
    cachedMessaging = getMessaging(firebaseApp);
  }
  return cachedMessaging;
}

async function fetchFcmToken(messaging, registration, source) {
  const vapidKey = getVapidKey();
  if (!vapidKey || !registration) return null;

  try {
    const token = await getToken(messaging, {
      vapidKey,
      serviceWorkerRegistration: registration,
    });
    logFcmToken(token, source);
    return token;
  } catch (e) {
    console.error(`[FCM] getToken failed (${source}):`, e?.code || e?.message || e);
    return null;
  }
}

/** Listen for messages forwarded from the service worker (background delivery + click nav). */
export function listenForServiceWorkerMessages(onPayload, onNavigate) {
  if (!("serviceWorker" in navigator)) return () => {};

  const handler = (event) => {
    if (event?.data?.type === "FCM_MESSAGE" && event.data.payload) {
      logNotificationPayload(event.data.payload, "FCM background → tab");
      onPayload?.(event.data.payload);
      return;
    }
    if (event?.data?.type === "NOTIFICATION_NAVIGATE" && event.data.path) {
      onNavigate?.(event.data.path);
    }
  };

  navigator.serviceWorker.addEventListener("message", handler);
  return () => navigator.serviceWorker.removeEventListener("message", handler);
}

export async function initFirebaseMessaging(onForegroundMessage) {
  const supported = await isSupported();
  if (!supported) {
    return { supported: false, messaging: null, token: null };
  }

  if (!getVapidKey()) {
    return { supported: true, messaging: null, token: null, registration: null };
  }

  // Register SW + onMessage first so messages are not missed while token loads
  const [registration] = await Promise.all([registerMessagingServiceWorker()]);
  const messaging = getOrCreateMessaging();

  onMessage(messaging, (payload) => {
    logNotificationPayload(payload, "FCM foreground");
    onForegroundMessage?.(payload);
  });

  let token = null;
  if (Notification.permission === "granted") {
    token = await fetchFcmToken(messaging, registration, "FCM init");
  }

  return { supported: true, messaging, token, registration };
}

export async function requestNotificationPermissionAndToken() {
  const supported = await isSupported();
  if (!supported) {
    return { ok: false, reason: "unsupported" };
  }

  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    return { ok: false, reason: "denied", permission };
  }

  if (!getVapidKey()) {
    return { ok: false, reason: "missing_vapid_key", permission };
  }

  const registration = await registerMessagingServiceWorker();
  const messaging = getOrCreateMessaging();
  const token = await fetchFcmToken(messaging, registration, "FCM enable");

  if (!token) {
    return { ok: false, reason: "token_empty", permission };
  }

  return { ok: true, token, permission, messaging };
}

export async function getOrRefreshFcmToken({ requestPermission = false } = {}) {
  const supported = await isSupported();
  if (!supported || !getVapidKey()) return null;

  if (Notification.permission === "granted") {
    const registration = await registerMessagingServiceWorker();
    const messaging = getOrCreateMessaging();
    return fetchFcmToken(messaging, registration, "FCM refresh");
  }

  if (requestPermission && Notification.permission === "default") {
    const result = await requestNotificationPermissionAndToken();
    return result.ok ? result.token : null;
  }

  return null;
}

export async function getFcmTokenForDebug() {
  const result = await requestNotificationPermissionAndToken();
  if (!result.ok) {
    console.error("[FCM] Failed:", result.reason);
    return null;
  }
  return result.token;
}

if (typeof window !== "undefined") {
  window.getFcmToken = getFcmTokenForDebug;
}

export function parseFcmPayload(payload) {
  const data = payload?.data || {};
  return {
    title: payload?.notification?.title || data.title || "Notification",
    body: payload?.notification?.body || data.body || data.message || "",
    data,
    type: data.type || null,
    link: resolveNotificationLink(data),
  };
}
